import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Avatar,
    Chip
} from '@mui/material';
import {MessageCircle} from 'react-feather';

/**
 * Format date with error handling
 */
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        console.error("Date formatting error:", error);
        return 'Invalid date';
    }
};

/**
 * Get initials for avatar
 */
const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

/**
 * CommentsList Component
 * Displays all comments for a ticket
 *
 * @param {Object} props
 * @param {Array} props.comments - Array of comment objects
 * @param {Object} props.ticket - The ticket object (optional)
 */
const CommentsList = ({comments = [], ticket}) => {
    // Handle different comment property structures
    const normalizeComment = (comment) => {
        return {
            id: comment.comment_id || comment.id,
            author: comment.author || comment.author_name || 'Unknown',
            authorType: comment.authorRole || comment.author_type,
            text: comment.text || comment.comment,
            timestamp: comment.timestamp || comment.date_created,
            isInternal: comment.is_internal || comment.isInternal || false
        };
    };

    // Get tenant name from ticket if available
    const tenantName = ticket?.tenant?.name || 'Tenant';

    // Filter out internal comments as tenants shouldn't see them
    const visibleComments = comments
        .filter(comment => !(comment.is_internal || comment.isInternal))
        .map(normalizeComment);

    return (
        <>
            {visibleComments.length > 0 ? (
                <Box sx={{mb: 3}}>
                    {visibleComments.map((comment) => (
                        <Paper
                            key={comment.id}
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 2,
                                borderRadius: '8px',
                                backgroundColor: comment.authorType === 'Tenant' || comment.author === tenantName ? '#EFF6FF' : '#F9FAFB',
                                border: '1px solid',
                                borderColor: comment.authorType === 'Tenant' || comment.author === tenantName ? '#DBEAFE' : '#E5E7EB'
                            }}
                        >
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                    <Avatar
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            fontSize: '0.75rem',
                                            mr: 1,
                                            bgcolor: comment.authorType === 'Tenant' || comment.author === tenantName ? '#3B82F6' : '#10B981'
                                        }}
                                    >
                                        {getInitials(comment.author)}
                                    </Avatar>
                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                        {comment.author}
                                        {comment.authorType && (
                                            <Typography component="span" variant="caption" color="textSecondary"
                                                        sx={{ml: 1}}>
                                                ({comment.authorType})
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                    {formatDate(comment.timestamp)}
                                </Typography>
                            </Box>
                            <Typography variant="body2">{comment.text}</Typography>
                        </Paper>
                    ))}
                </Box>
            ) : (
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        mb: 3
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        No comments yet
                    </Typography>
                </Box>
            )}
        </>
    );
};

export default CommentsList;